/**
 * Per-industry SEO metadata used by the public /industries/* pages.
 *
 * Each entry maps an industry slug (matching the route segment) to the
 * absolute URL of its OG image and a concise schema.org service descriptor.
 * Centralized here so we have one source of truth across IndustryPlaceholderPage
 * and any standalone industry pages (e.g. SecurityIndustry).
 */

const STORAGE_BASE =
  "https://hlhneneadrsqtfdawhld.supabase.co/storage/v1/object/public/og-images";

export interface IndustrySeo {
  /** Canonical industry display name (matches the page H1). */
  name: string;
  /** schema.org serviceType — what kind of service this is. */
  serviceType: string;
  /** Plain-language pitch (~25 words) used as JSON-LD description. */
  serviceDescription: string;
  /** Public URL of the OG/hero image for social sharing. */
  ogImage: string;
}

/** Lowest publicly listed price (Starter tier, $/month). */
export const PRICE_FROM_USD = 19;

/** Pricing page URL JSON-LD offers point to. */
export const OFFER_URL = "https://www.sentinelforgesystems.com/pricing";

/**
 * Lookup keyed by industry slug used in the route, e.g. /industries/cleaning.
 * Slug must match the slugification used in IndustryPlaceholderPage:
 *   name.toLowerCase().replace(/&/g, "-").replace(/\s+/g, "-")
 */
export const INDUSTRY_SEO: Record<string, IndustrySeo> = {
  security: {
    name: "Security",
    serviceType: "Security Proposal Software",
    serviceDescription:
      "AI-powered proposal builder for private security companies — generate threat assessments, staffing plans, and client-ready proposals in minutes.",
    ogImage: `${STORAGE_BASE}/og-security.jpg`,
  },
  "commercial-cleaning": {
    name: "Commercial Cleaning",
    serviceType: "Commercial Cleaning Proposal Software",
    serviceDescription:
      "Build commercial cleaning proposals with square-footage pricing, frequency calculators, and client-ready PDF exports — all in one platform.",
    ogImage: `${STORAGE_BASE}/og-cleaning.jpg`,
  },
  "event-staffing": {
    name: "Event Staffing",
    serviceType: "Event Staffing Proposal Software",
    serviceDescription:
      "Create event staffing proposals with multi-role crew planning, overtime multipliers, and instant cost breakdowns for any size event.",
    ogImage: `${STORAGE_BASE}/og-event-staffing.jpg`,
  },
  "private-investigation": {
    name: "Private Investigation",
    serviceType: "Private Investigation Proposal Software",
    serviceDescription:
      "Generate discreet, professional PI proposals for surveillance, OSINT, and background investigations with built-in case-type templates.",
    ogImage: `${STORAGE_BASE}/og-private-investigation.jpg`,
  },
  "landscaping-groundskeeping": {
    name: "Landscaping & Groundskeeping",
    serviceType: "Landscaping Proposal Software",
    serviceDescription:
      "Build seasonal landscaping and groundskeeping proposals with crew sizing, equipment, and recurring-visit pricing in minutes.",
    ogImage: `${STORAGE_BASE}/og-landscaping.jpg`,
  },
  "pest-control": {
    name: "Pest Control",
    serviceType: "Pest Control Proposal Software",
    serviceDescription:
      "Generate pest control proposals with treatment plans, technician staffing, and recurring service pricing tailored to commercial and residential clients.",
    ogImage: `${STORAGE_BASE}/og-pest-control.jpg`,
  },
  "facilities-maintenance": {
    name: "Facilities Maintenance",
    serviceType: "Facilities Maintenance Proposal Software",
    serviceDescription:
      "Create facilities maintenance proposals with SLA-driven response times, technician roles, and per-system coverage breakdowns.",
    ogImage: `${STORAGE_BASE}/og-facilities-maintenance.jpg`,
  },
  "property-management-services": {
    name: "Property Management Services",
    serviceType: "Property Management Proposal Software",
    serviceDescription:
      "Build property management proposals covering leasing, maintenance, and accounting with transparent fee structures and scope.",
    ogImage: `${STORAGE_BASE}/og-property-management.jpg`,
  },
  "it-managed-services": {
    name: "IT Managed Services",
    serviceType: "IT MSP Proposal Software",
    serviceDescription:
      "Generate per-seat MSP proposals with helpdesk, monitoring, and security tiers — complete with SLA terms and recurring pricing.",
    ogImage: `${STORAGE_BASE}/og-it-managed-services.jpg`,
  },
  "home-services": {
    name: "Home Services",
    serviceType: "Home Services Proposal Software",
    serviceDescription:
      "Create homeowner-ready proposals for HVAC, plumbing, electrical, and handyman work with materials and labor automatically priced.",
    ogImage: `${STORAGE_BASE}/og-home-services.jpg`,
  },
  "construction-services": {
    name: "Construction Services",
    serviceType: "Construction Proposal Software",
    serviceDescription:
      "Build construction proposals and CSI-style cost breakdowns for renovations, build-outs, and new construction with trade-by-trade pricing.",
    ogImage: `${STORAGE_BASE}/og-construction-services.jpg`,
  },
  "power-washing": {
    name: "Power Washing",
    serviceType: "Power Washing Proposal Software",
    serviceDescription:
      "Build technical power washing proposals with surface-specific PSI, soft-wash chemistry, EPA-compliant scope, and per-sq-ft pricing in minutes.",
    ogImage: `${STORAGE_BASE}/og-power-washing.jpg`,
  },
};

const DEFAULT_OG =
  "https://hlhneneadrsqtfdawhld.supabase.co/storage/v1/object/public/og-images/sentinelforge-og.png";

/**
 * Resolve SEO data by slug. Returns a safe fallback so unknown slugs (or
 * future industries) still get usable meta tags.
 */
export function getIndustrySeo(slug: string): IndustrySeo {
  return (
    INDUSTRY_SEO[slug] ?? {
      name: slug,
      serviceType: "Business Proposal Software",
      serviceDescription:
        "AI-powered proposal and contract software for service businesses. Create branded proposals in minutes.",
      ogImage: DEFAULT_OG,
    }
  );
}
